import { useEffect, useRef, useState } from "react";
import performRequest from "network/perform-request";
import { parseLeaguePaginatedResponse } from "components/pages/Gestion/gestion-pagination";

const SEARCH_DEBOUNCE_MS = 400;

/**
 * Server-paginated list for the financial tabs. Filters and search are sent to
 * the API; changing any of them resets to the first page. `reloadKey` forces a
 * refetch of the current page (e.g. after a payment is registered).
 */
export function useFinancialPagedList({
  customerId,
  user,
  route,
  search,
  filters,
  initialPageSize,
  reloadKey,
  onError,
}) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [debouncedSearch, setDebouncedSearch] = useState(search.trim());
  const [state, setState] = useState({
    results: [],
    totalPages: 1,
    totalResults: 0,
    raw: null,
    loading: true,
  });

  const userRef = useRef(user);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    userRef.current = user;
    onErrorRef.current = onError;
  }, [onError, user]);

  useEffect(() => {
    const timeout = setTimeout(
      () => setDebouncedSearch(search.trim()),
      search.trim() ? SEARCH_DEBOUNCE_MS : 0,
    );
    return () => clearTimeout(timeout);
  }, [search]);

  const filtersKey = JSON.stringify(filters);
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, filtersKey, customerId]);

  useEffect(() => {
    if (!customerId || !userRef.current) return undefined;
    let cancelled = false;
    setState((current) => ({ ...current, loading: true }));

    performRequest(
      "GET",
      route(customerId),
      {
        ...JSON.parse(filtersKey),
        search: debouncedSearch || undefined,
        page,
        page_size: pageSize,
      },
      true,
      userRef.current,
    )
      .then((response) => {
        if (cancelled) return;
        const parsed = parseLeaguePaginatedResponse(response.data);
        setState({
          results: parsed.results,
          totalPages: parsed.totalPages,
          totalResults: parsed.totalResults,
          raw: response.data,
          loading: false,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        onErrorRef.current?.(error);
        setState({
          results: [],
          totalPages: 1,
          totalResults: 0,
          raw: null,
          loading: false,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [
    customerId,
    route,
    filtersKey,
    debouncedSearch,
    page,
    pageSize,
    reloadKey,
  ]);

  return {
    ...state,
    page,
    pageSize,
    setPage,
    setPageSize: (size) => {
      setPageSize(size);
      setPage(0);
    },
  };
}
